package at.theatervogelweide.admin.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import at.theatervogelweide.admin.contract.Account
import at.theatervogelweide.admin.contract.AdminResult
import at.theatervogelweide.admin.contract.ReconnectingRpcClient
import at.theatervogelweide.admin.contract.ServerInfo
import at.theatervogelweide.admin.contract.ServerInfoService
import at.theatervogelweide.admin.web.components.AdminShell
import at.theatervogelweide.admin.web.components.LocalBetaSite
import at.theatervogelweide.admin.web.components.LocalPublicSite
import at.theatervogelweide.admin.web.components.NavigationEntry
import at.theatervogelweide.admin.web.components.NavigationGroup
import at.theatervogelweide.admin.web.components.PublicSite
import at.theatervogelweide.admin.web.components.UnsavedChangesDialog
import at.theatervogelweide.admin.web.components.UnsavedChangesGuard
import at.theatervogelweide.admin.web.components.rememberSplitController
import at.theatervogelweide.admin.web.screens.ChangePasswordScreen
import at.theatervogelweide.admin.web.screens.ConnectionsScreen
import at.theatervogelweide.admin.web.screens.NotFoundScreen
import at.theatervogelweide.admin.web.screens.OverviewScreen
import at.theatervogelweide.admin.web.screens.SetPasswordScreen
import at.theatervogelweide.admin.web.screens.SignInScreen
import at.theatervogelweide.admin.web.screens.UnavailableScreen
import at.theatervogelweide.admin.web.screens.accounts.AccountsScreen
import at.theatervogelweide.admin.web.screens.acts.ActEditorScreen
import at.theatervogelweide.admin.web.screens.acts.ActsListState
import at.theatervogelweide.admin.web.screens.acts.ActsScreen
import at.theatervogelweide.admin.web.screens.board.BoardScreen
import at.theatervogelweide.admin.web.screens.sponsors.SponsorsScreen
import at.theatervogelweide.admin.web.screens.club.ClubScreen
import at.theatervogelweide.admin.web.screens.imprint.ImprintScreen
import at.theatervogelweide.admin.web.screens.news.NewsScreen
import at.theatervogelweide.admin.web.screens.people.PeopleScreen
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.rpc.withService
import kotlin.time.Duration.Companion.seconds

private sealed interface SessionState {
    data object Loading : SessionState

    data object SignedOut : SessionState

    /** The server couldn't say whether there is a session; the cookie may well still be valid. */
    data object Unavailable : SessionState

    data class SignedIn(val account: Account) : SessionState
}

/**
 * Only screens that exist: Übersicht, then what the website shows and the administration (#144); "Konten" only for
 * superusers (#66). "Verein" only feeds the new website until the switch-over (#115).
 */
internal fun navigation(account: Account) = listOf(
    NavigationGroup(null, listOf(NavigationEntry("Übersicht", Route.Overview))),
    NavigationGroup(
        "Website",
        listOf(
            NavigationEntry("Neuigkeiten", Route.News()),
            NavigationEntry("Stücke & Termine", Route.Acts()),
            NavigationEntry("Personen", Route.People()),
            NavigationEntry("Vorstand", Route.Board()),
            NavigationEntry("Verein", Route.Club, beta = true),
            NavigationEntry("Sponsoren", Route.Sponsors()),
            NavigationEntry("Impressum", Route.Imprint),
        ),
    ),
    NavigationGroup(
        "Verwaltung",
        listOfNotNull(
            NavigationEntry("Verbundene Anwendungen", Route.Connections),
            NavigationEntry("Konten", Route.Accounts()).takeIf { account.superuser },
        ),
    ),
)

@Composable
fun App(api: AdminApi, router: Router) {
    val scope = rememberCoroutineScope()
    var session by remember { mutableStateOf<SessionState>(SessionState.Loading) }
    var signOutFailed by remember { mutableStateOf(false) }
    var loadAttempt by remember { mutableStateOf(0) }

    LaunchedEffect(loadAttempt) {
        // only a confirmed "no session" (401) leads to the sign-in screen
        val loaded = runCatching { api.currentAccount() }.fold(
            onSuccess = { account -> account?.let { SessionState.SignedIn(it) } ?: SessionState.SignedOut },
            onFailure = { SessionState.Unavailable },
        )
        // a one-time link may have signed someone in meanwhile (#66)
        if (session == SessionState.Loading) {
            session = loaded
        }
    }

    // a one-time link works with or without a session; using it signs its account in (#66)
    if (router.current == Route.SetPassword) {
        SetPasswordScreen(
            api,
            onSignedIn = { account ->
                session = account?.let { SessionState.SignedIn(it) } ?: SessionState.SignedOut
                router.navigate(if (account == null) Route.SignIn() else Route.Overview, replace = true)
            },
            onSignIn = {
                session = SessionState.Loading
                loadAttempt++
                router.navigate(Route.SignIn(), replace = true)
            },
        )
        return
    }

    when (val state = session) {
        SessionState.Loading -> Unit

        SessionState.Unavailable -> UnavailableScreen {
            session = SessionState.Loading
            loadAttempt++
        }

        SessionState.SignedOut -> {
            val current = router.current
            LaunchedEffect(current) {
                if (current !is Route.SignIn) {
                    router.navigate(Route.SignIn(returnTo = current), replace = true)
                }
            }

            SignInScreen(api) { account ->
                session = SessionState.SignedIn(account)
                router.navigate((current as? Route.SignIn)?.returnTo ?: Route.Overview, replace = true)
            }
        }

        is SessionState.SignedIn -> {
            // one RPC client per sign-in, which connects again after the WebSocket closed (#53); every upgrade request
            // carries the session cookie
            val rpc = remember(state) {
                ReconnectingRpcClient(connect = api::rpc) {
                    // the browser hides the upgrade's status: only a confirmed "no session" (401) leads to sign-in
                    scope.launch {
                        val signedOut = runCatching { api.currentAccount() == null }.getOrDefault(false)
                        if (signedOut && session == state) {
                            session = SessionState.SignedOut
                        }
                    }
                }
            }
            DisposableEffect(rpc) { onDispose { rpc.close() } }

            // the links to the public site and its beta (#144) wait for their addresses (#64); a failed call is tried again
            var serverInfo by remember(rpc) { mutableStateOf<ServerInfo?>(null) }
            LaunchedEffect(rpc) {
                val service = rpc.withService<ServerInfoService>()
                while (serverInfo == null) {
                    when (val result = rpcCall { service.serverInfo() }) {
                        is AdminResult.Success -> serverInfo = result.value
                        is AdminResult.Failure -> delay(5.seconds)
                    }
                }
            }
            val publicSite = remember(serverInfo) { serverInfo?.let { PublicSite(it.publicSiteUrl) } }
            val betaSite = remember(serverInfo) { serverInfo?.betaSiteUrl?.let { PublicSite(it) } }

            val guard = remember(state) { UnsavedChangesGuard() }
            // as the shell shows it; saving one's own account in Konten may change it (#66)
            var account by remember(state) { mutableStateOf(state.account) }
            DisposableEffect(guard) {
                router.guard = guard
                onDispose { router.guard = null }
            }
            UnsavedChangesDialog(guard)

            LaunchedEffect(router.current) {
                if (router.current is Route.SignIn) {
                    router.navigate(Route.Overview, replace = true)
                }
            }

            // Stücke & Termine comes back from its editors as it was left; other screens start it afresh
            var actsList by remember(state) { mutableStateOf(ActsListState()) }
            LaunchedEffect(router.current) {
                if (router.current !is Route.Acts) {
                    actsList = ActsListState()
                }
            }

            CompositionLocalProvider(LocalPublicSite provides publicSite, LocalBetaSite provides betaSite) {
                AdminShell(
                    account = account,
                    navigation = navigation(account),
                    current = router.current,
                    onNavigate = { route -> guard.proceed { router.navigate(route) } },
                    onSignOut = {
                        guard.proceed {
                            scope.launch {
                                // only a confirmed sign-out counts: otherwise the cookie would still sign in the next visitor
                                if (runCatching { api.signOut() }.isSuccess) {
                                    signOutFailed = false
                                    session = SessionState.SignedOut
                                    router.navigate(Route.SignIn(), replace = false)
                                } else {
                                    signOutFailed = true
                                }
                            }
                        }
                    },
                    signOutError = "Abmelden hat nicht geklappt. Bitte versuche es noch einmal.".takeIf { signOutFailed },
                ) {
                    when (val route = router.current) {
                        Route.Overview -> OverviewScreen(rpc) { target -> guard.proceed { router.navigate(target) } }
                        is Route.News -> NewsScreen(
                            rpc,
                            api,
                            guard,
                            rememberSplitController(router, guard) { id, new -> Route.News(id, new) },
                            route.selectedId,
                            route.new,
                        )
                        is Route.Acts -> when {
                            route.new || route.actId != null -> ActEditorScreen(rpc, api, guard, route.actId) { target, replace ->
                                router.navigate(target, replace)
                            }

                            else -> ActsScreen(rpc, api, actsList) { target -> guard.proceed { router.navigate(target) } }
                        }
                        is Route.People -> PeopleScreen(
                            rpc,
                            api,
                            guard,
                            rememberSplitController(router, guard) { id, new -> Route.People(id, new) },
                            route.selectedId,
                            startNew = route.new,
                        ) { id -> guard.proceed { router.navigate(Route.Acts(actId = id)) } }
                        is Route.Board -> BoardScreen(
                            rpc,
                            api,
                            guard,
                            rememberSplitController(router, guard) { id, new -> Route.Board(id, new) },
                            route.selectedId,
                            startNew = route.new,
                        ) { id -> guard.proceed { router.navigate(Route.People(id)) } }
                        is Route.Sponsors -> SponsorsScreen(
                            rpc,
                            api,
                            guard,
                            rememberSplitController(router, guard) { id, new -> Route.Sponsors(id, new) },
                            route.selectedId,
                            startNew = route.new,
                        )
                        Route.Club -> ClubScreen(rpc, guard, editable = account.superuser) { target -> guard.proceed { router.navigate(target) } }
                        Route.Imprint -> ImprintScreen(rpc, guard, editable = account.superuser) { target -> guard.proceed { router.navigate(target) } }
                        Route.Connections -> ConnectionsScreen(rpc)
                        is Route.Accounts -> if (account.superuser) {
                            AccountsScreen(
                                rpc,
                                guard,
                                rememberSplitController(router, guard) { id, new -> Route.Accounts(id, new) },
                                route.selectedId,
                                startNew = route.new,
                            ) { scope.launch { runCatching { api.currentAccount() }.getOrNull()?.let { account = it } } }
                        } else {
                            NotFoundScreen(router::navigate)
                        }
                        Route.ChangePassword -> ChangePasswordScreen(api, guard) { account = it }
                        Route.SetPassword -> Unit
                        is Route.NotFound -> NotFoundScreen(router::navigate)
                        is Route.SignIn -> Unit
                    }
                }
            }
        }
    }
}
